import React, { useState, useEffect, useRef } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  Grid,
  Card,
  Stack,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Divider,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArticleIcon from "@mui/icons-material/Article";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";

const MAX_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB
const ACCEPTED = ".pdf,.doc,.docx";
const ACCEPTED_MIME = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const formatBytes = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const fileIcon = (file) => {
  if (!file) return null;
  const name = file.name || file;
  if (typeof name === "string" && name.toLowerCase().endsWith(".pdf")) {
    return <PictureAsPdfIcon sx={{ fontSize: 32, color: "#e53935" }} />;
  }
  return <ArticleIcon sx={{ fontSize: 32, color: "#1565c0" }} />;
};

function WorkPlanAdd() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const fileInputRef = useRef(null);

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedFile, setSelectedFile] = useState(null); // new file to upload
  const [existingFileUrl, setExistingFileUrl] = useState(null); // current URL in edit mode
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [duplicateWarning, setDuplicateWarning] = useState(null); // { label: "Feb 2026", id: 3 }
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewRaw, setPreviewRaw] = useState(null);

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleViewFile = (url) => {
    if (!url) return;
    setPreviewRaw(url);
    const lower = url.split("?")[0].toLowerCase();
    if (lower.endsWith(".doc") || lower.endsWith(".docx")) {
      setPreviewUrl(`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`);
    } else {
      setPreviewUrl(url);
    }
    setPreviewOpen(true);
  };

  const handleDownloadFile = async (url) => {
    if (!url) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = url.split("/").pop().split("?")[0] || "workplan";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSnackbar({ open: true, message: "Download started!", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Download failed.", severity: "error" });
    }
  };

  // Fetch current user
  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  };

  const { data: meData } = useQuery({ queryKey: ["me"], queryFn: me, retry: false });
  const isAdmin = meData?.role_id === 1 || meData?.role_id === 2;

  // Load existing work plan in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchWorkPlan = async () => {
        try {
          setLoading(true);
          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/work-plans/get/${id}`,
            { withCredentials: true }
          );
          if (res.data.data) {
            const wp = res.data.data;
            setSelectedDate(dayjs(wp.month + "-01"));
            setExistingFileUrl(wp.workplan_file);
          }
        } catch {
          setSnackbar({ open: true, message: "Failed to load work plan.", severity: "error" });
        } finally {
          setLoading(false);
        }
      };
      fetchWorkPlan();
    }
  }, [isEditMode, id]);

  // Block non-admins
  if (meData && !isAdmin) {
    return (
      <DashboardLayout>
        <MDBox display="flex" justifyContent="center" alignItems="center" height="80vh" flexDirection="column">
          <MDTypography variant="h4" color="error" fontWeight="bold">403 - Unauthorized</MDTypography>
          <MDTypography variant="body2" color="text">Only admins can manage work plans.</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  const validateFile = (file) => {
    if (!ACCEPTED_MIME.includes(file.type)) {
      setSnackbar({ open: true, message: "Only PDF or Word (.doc / .docx) files are allowed.", severity: "error" });
      return false;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setSnackbar({ open: true, message: "File must be 25 MB or smaller.", severity: "error" });
      return false;
    }
    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (validateFile(file)) setSelectedFile(file);
    e.target.value = ""; // reset input so same file can be picked again
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (validateFile(file)) setSelectedFile(file);
  };

  const handleSubmit = async () => {
    if (!meData?.user_id) {
      setSnackbar({ open: true, message: "User authentication failed.", severity: "error" });
      return;
    }
    if (!selectedFile && !isEditMode) {
      setSnackbar({ open: true, message: "Please select a file to upload.", severity: "warning" });
      return;
    }
    if (!selectedFile && isEditMode) {
      setSnackbar({ open: true, message: "Please select a replacement file to update.", severity: "warning" });
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("month", selectedDate.format("YYYY-MM"));
      formData.append("user_id", meData.user_id);
      formData.append("file", selectedFile);

      if (isEditMode) {
        const res = await axios.put(
          `${process.env.REACT_APP_API_URL}/work-plans/update/${id}`,
          formData,
          { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
        );
        setSnackbar({ open: true, message: res.data.message || "Work plan updated successfully!", severity: "success" });
      } else {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/work-plans/add`,
          formData,
          { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
        );
        setSnackbar({ open: true, message: res.data.message || "Work plan uploaded successfully!", severity: "success" });
      }
      setTimeout(() => navigate("/work-plan/list"), 2000);
    } catch (error) {
      if (error.response?.status === 409) {
        const monthLabel = selectedDate.format("MMM YYYY");
        const existingId = error.response.data.existingId;
        setDuplicateWarning({ label: monthLabel, id: existingId });
      } else {
        setSnackbar({ open: true, message: error.response?.data?.message || "Something went wrong.", severity: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <MDBox mt={4} mb={3}>
        {/* Top Bar */}
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h4" fontWeight="bold">
            {isEditMode ? "Edit Work Plan" : "Upload Work Plan"}
          </MDTypography>
          <MDButton variant="outlined" color="dark" onClick={() => navigate("/work-plan/list")} size="small">
            ← Back to List
          </MDButton>
        </MDBox>

        <Card>
          <MDBox p={{ xs: 2, sm: 3, md: 4 }}>
            <Stack spacing={4}>
              {/* Month Selection */}
              <MDBox borderBottom="1px solid #eee" pb={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={1.5}>
                  Select Work Plan Month
                </MDTypography>
                <Grid container>
                  <Grid item xs={12} sm={8} md={4}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Month and Year"
                        views={["year", "month"]}
                        value={selectedDate}
                        onChange={(newValue) => {
                          setSelectedDate(newValue);
                          setDuplicateWarning(null); // clear warning on month change
                        }}
                        disabled={isEditMode}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            InputProps: { readOnly: true, sx: { fontSize: "14px", height: "45px" } },
                          },
                        }}
                      />
                    </LocalizationProvider>
                    {isEditMode && (
                      <MDTypography variant="caption" color="text" sx={{ mt: 0.5, display: "block" }}>
                        Month cannot be changed in edit mode.
                      </MDTypography>
                    )}
                  </Grid>
                </Grid>
              </MDBox>

              {/* Duplicate Month Warning Banner */}
              {!isEditMode && duplicateWarning && (
                <MDBox
                  sx={{
                    p: 2,
                    backgroundColor: "#fff8e1",
                    border: "1px solid #ffe082",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <MDBox>
                    <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#e65100" }}>
                      ⚠️ {duplicateWarning.label} — Work Plan Already Exists
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#795548" }}>
                      A work plan for {duplicateWarning.label} has already been uploaded. If you want to make changes, please edit the existing one.
                    </MDTypography>
                  </MDBox>
                  <MDButton
                    variant="contained"
                    size="small"
                    onClick={() => navigate(`/work-plan/edit/${duplicateWarning.id}`)}
                    sx={{ backgroundColor: "#e65100", color: "#fff", whiteSpace: "nowrap", "&:hover": { backgroundColor: "#bf360c" } }}
                  >
                    Edit Existing
                  </MDButton>
                </MDBox>
              )}

              {/* File Upload Section */}
              <MDBox>
                <MDTypography variant="h6" fontWeight="medium" mb={1.5}>
                  {isEditMode ? "Replace Work Plan File" : "Upload Work Plan File"}
                </MDTypography>

                {/* Existing file info in edit mode */}
                {isEditMode && existingFileUrl && (
                  <MDBox
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #a5d6a7",
                      borderRadius: 2,
                      backgroundColor: "#f1f8e9",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <CheckCircleIcon sx={{ color: "#2e7d32", flexShrink: 0 }} />
                    <MDBox flex={1} sx={{ overflow: "hidden" }}>
                      <MDTypography variant="caption" fontWeight="bold" color="success" sx={{ display: "block" }}>
                        Current File
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", color: "#333" }}
                      >
                        {existingFileUrl.split("/").pop().split("?")[0]}
                      </MDTypography>
                    </MDBox>
                    <MDButton
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleViewFile(existingFileUrl)}
                    >
                      View
                    </MDButton>
                  </MDBox>
                )}

                {/* Drag-and-Drop Zone */}
                <Box
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    border: `2px dashed ${dragOver ? "#2e7d32" : selectedFile ? "#66bb6a" : "#bdbdbd"}`,
                    borderRadius: 3,
                    p: { xs: 3, md: 5 },
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: dragOver ? "#f1f8e9" : selectedFile ? "#f9fbe7" : "#fafafa",
                    transition: "all 0.2s ease",
                    "&:hover": { borderColor: "#2e7d32", backgroundColor: "#f1f8e9" },
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />

                  {selectedFile ? (
                    <MDBox display="flex" flexDirection="column" alignItems="center" gap={1.5}>
                      {fileIcon(selectedFile)}
                      <MDBox>
                        <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#2e7d32" }}>
                          {selectedFile.name}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          {formatBytes(selectedFile.size)}
                        </MDTypography>
                      </MDBox>
                      <MDButton
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                      >
                        Remove
                      </MDButton>
                    </MDBox>
                  ) : (
                    <MDBox display="flex" flexDirection="column" alignItems="center" gap={1.5}>
                      <CloudUploadIcon sx={{ fontSize: 48, color: "#9e9e9e" }} />
                      <MDTypography variant="subtitle1" fontWeight="medium" color="text">
                        Drag & drop your file here, or click to browse
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Accepted: PDF, DOC, DOCX &bull; Max size: 25 MB
                      </MDTypography>
                    </MDBox>
                  )}
                </Box>


              </MDBox>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }} />
                <MDBox display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="flex-end" gap={2}>
                  <MDButton
                    variant="outlined"
                    color="error"
                    onClick={() => navigate("/work-plan/list")}
                    sx={{ minWidth: { xs: "100%", sm: 160 }, borderRadius: 2 }}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{ minWidth: { xs: "100%", sm: 200 }, borderRadius: 2 }}
                  >
                    {loading
                      ? isEditMode ? "Updating..." : "Saving..."
                      : isEditMode ? "Update Work Plan" : "Save Work Plan"}
                  </MDButton>
                </MDBox>
              </Grid>
            </Stack>
          </MDBox>
        </Card>
      </MDBox>

      {/* File Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: "90vh", borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <MDTypography variant="h6" fontWeight="bold">Current Work Plan File</MDTypography>
          <MDBox display="flex" gap={1} alignItems="center">
            <MDButton size="small" variant="gradient" color="success" startIcon={<DownloadIcon />} onClick={() => handleDownloadFile(previewRaw)}>
              Download
            </MDButton>
            <IconButton onClick={() => setPreviewOpen(false)} size="small"><CloseIcon /></IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: "hidden" }}>
          {previewUrl && (
            <iframe
              src={previewUrl}
              title="Work Plan Preview"
              width="100%"
              height="100%"
              style={{ border: "none", display: "block", minHeight: "70vh" }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <MDButton variant="outlined" color="dark" onClick={() => setPreviewOpen(false)} size="small">Close</MDButton>
          <MDButton variant="gradient" color="success" startIcon={<DownloadIcon />} onClick={() => handleDownloadFile(previewRaw)} size="small">Download</MDButton>
        </DialogActions>
      </Dialog>

      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: "100%", color: "white !important" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default WorkPlanAdd;
